/**
 * Utilities for number/string formatting.
 */

interface FormatOptions {
  decimalPlaces?: number
  decimalSeparator?: string
  thousandsSeparator?: string
  showPlus?: boolean
  zeroCurrencyText?: string
}

// http://stackoverflow.com/questions/149055/how-can-i-format-numbers-as-money-in-javascript/149099#149099
// Modified to take a value as a default argument, optional options as a hash, and prepend a dollar sign.
const formatMoney = function (
  value: number,
  options: FormatOptions = {}
): string {
  // When `zeroCurrencyText` is populated and the `value` given to formatMoney is 0 we'll replace the 0 with
  // `zeroCurrencyText`. Ensure when providing a replacement ensure it makes semantic sense.
  // e.g. Passing 'FREE' with the discount price will not make sense as it is a discount quantity and not a free item.
  const { zeroCurrencyText } = options
  if (value === 0 && zeroCurrencyText) {
    // Show text instead of $0.00
    return zeroCurrencyText
  } else {
    const decimalPlaces = isNaN(
      (options.decimalPlaces = Math.abs(options.decimalPlaces!))
    )
      ? 2
      : options.decimalPlaces
    const decimalSeparator = options.decimalSeparator || '.'
    const thousandsSeparator = options.thousandsSeparator || ','
    const plusMinus = value < 0 ? '-' : options.showPlus ? '+' : ''
    const valueString = Math.abs(+value || 0).toFixed(decimalPlaces)
    value = Number(valueString)
    const i = parseInt(valueString) + ''
    const j = i.length > 3 ? i.length % 3 : 0
    const first = plusMinus + '$'
    const second = !j ? '' : i.substr(0, j) + thousandsSeparator
    const third = i
      .substr(j)
      .replace(/(\d{3})(?=\d)/g, '$1' + thousandsSeparator)
    const fourth = !decimalPlaces
      ? ''
      : decimalSeparator +
        Math.abs(value - Number(i))
          .toFixed(decimalPlaces)
          .slice(2)
    return first + second + third + fourth
  }
}

const decimalToPercentage = function (decimalValue: number): number {
  if (decimalValue < 1 && decimalValue > 0) {
    return decimalValue * 100
  } else {
    return decimalValue
  }
}

const isNumeric = (n: string | number): boolean =>
  !isNaN(parseFloat(n as string)) && isFinite(n as number)

const decimalAdjust = function (
  type: 'round' | 'ceil' | 'floor',
  value: number,
  exp?: number
): number {
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math[type](value)
  }
  value = +value
  exp = +exp
  // If the value is not a number or the exp is not an integer...
  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN
  }
  const valueArray1 = value.toString().split('e')
  value = Math[type](
    +(valueArray1[0] + 'e' + (valueArray1[1] ? +valueArray1[1] - exp : -exp))
  )
  const valueArray2 = value.toString().split('e')
  return +(
    valueArray2[0] +
    'e' +
    (valueArray2[1] ? +valueArray2[1] + exp : exp)
  )
}

// Decimal round
const roundTwoDecimal = (value: number, exp: number = -2): number =>
  decimalAdjust('round', value, exp)

const parseFloatWithDollarSign = (priceWithDollarSign: string): number =>
  parseFloat(priceWithDollarSign.substr(1))

const isAllLowerOrUpper = function (
  text: string,
  options: { ignoreUpToNUpperChars: number } = { ignoreUpToNUpperChars: 0 }
): boolean {
  // Only use alpha characters when checking ignoreUpToNUpperChars so something like P.O. Box will behave as expected
  const textAlphaOnly = text.replace(/[^A-Za-z]/g, '')
  // This catches when the text is empty
  if (
    (textAlphaOnly === textAlphaOnly.toUpperCase() &&
      textAlphaOnly.length <= options.ignoreUpToNUpperChars) ||
    // or when things that aren't case sensitive are entered
    /^([^A-Za-z])+$/i.test(text)
  ) {
    return false
  } else {
    return text === text.toLowerCase() || text === text.toUpperCase()
  }
}

export default {
  formatMoney,
  parseFloatWithDollarSign,
  isNumeric,
  isAllLowerOrUpper,
  roundTwoDecimal,
  decimalToPercentage
}
